// src/services/jobService.js
import { supabase } from '../lib/supabase';

const SAVED_JOBS_TABLE = 'saved_jobs';
const ID_FIELD = 'cis_fuss_id'; // Using cis_fuss_id as the stable numeric identifier

/**
 * Get all saved job IDs for a user
 * @param {string} userId - The user ID
 * @returns {Promise<string[]>} Array of job IDs
 */
export const getSavedJobIds = async (userId) => {
  if (!userId) {
    console.log('getSavedJobIds: No userId provided, returning empty array');
    return [];
  }
  
  console.log(`getSavedJobIds: Fetching saved jobs for user ${userId}`);
  
  try {
    // Output the table name for debugging
    console.log(`getSavedJobIds: Using table '${SAVED_JOBS_TABLE}'`);
    
    // Query saved jobs
    const { data, error } = await supabase
      .from(SAVED_JOBS_TABLE)
      .select(ID_FIELD)
      .eq('user_id', userId);
      
    if (error) {
      console.error('getSavedJobIds: Error fetching saved jobs:', error);
      return [];
    }
    
    // Extract job IDs safely and ensure they're all numbers for consistent comparison
    const jobIds = data ? data.map(item => {
      // Handle the case where cis_fuss_id is 0 (treat as valid ID)
      const rawId = item[ID_FIELD];
      if (rawId !== undefined && rawId !== null) {
        // Ensure all IDs are numbers
        return Number(rawId);
      }
      return null;
    }).filter(id => id !== null && id !== undefined) : [];
    
    console.log(`getSavedJobIds: Found ${jobIds.length} saved jobs for user ${userId}:`, jobIds);
    return jobIds;
  } catch (error) {
    console.error('getSavedJobIds: Unexpected error:', error);
    return [];
  }
};

/**
 * Save a job for a user
 * @param {string} userId - The user ID
 * @param {string|Object} job - The job ID string or job object
 * @returns {Promise<boolean>} Success state
 */
export const saveJob = async (userId, job) => {
  if (!userId) {
    console.error('saveJob: userId is missing');
    return false;
  }
  
  // Add detailed logging about the job parameter
  if (typeof job === 'object') {
    console.log('saveJob: Full job object received:', job);
    console.log('saveJob: Available fields:', Object.keys(job));
    
    // Extra safety check for semantic search jobs
    if (job.job_id && !job.cis_fuss_id) {
      // Look for any jobs with this job_id in clean.json
      // This is a safety measure for semantic search which might use job_id instead of cis_fuss_id
      console.log('saveJob: This job has job_id but no cis_fuss_id. This can happen with semantic search results.');
    }
  }
  
  // Extract cis_fuss_id (no fallbacks)
  const jobId = typeof job === 'object' ? job.cis_fuss_id : job;
  console.log(`saveJob: Using cis_fuss_id: ${jobId}`);
  
  // Make sure we have a valid job ID - not undefined, null, or 0
  if (jobId === undefined || jobId === null || jobId === 0) {
    console.error(`saveJob: Invalid cis_fuss_id provided: ${jobId}`);
    return false;
  }
  
  try {
    // Check if already saved
    console.log(`saveJob: Checking if job ${jobId} is already saved for user ${userId}`);
    
    // Use a simpler query without .single() to avoid potential errors
    const { data, error: checkError } = await supabase
      .from(SAVED_JOBS_TABLE)
      .select('*')
      .eq('user_id', userId)
      .eq(ID_FIELD, jobId);
      
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('saveJob: Error checking if job is already saved:', checkError);
    }
    
    // If already saved (data exists and has items), return success
    if (data && data.length > 0) {
      console.log(`saveJob: Job ${jobId} is already saved for user ${userId}`);
      return true;
    }
    
    // Otherwise insert new record
    console.log(`saveJob: Saving job ${jobId} for user ${userId}`);
    const { error: insertError } = await supabase
      .from(SAVED_JOBS_TABLE)
      .insert([{
        user_id: userId,
        [ID_FIELD]: jobId,
        saved_at: new Date().toISOString(),
      }]);
      
    if (insertError) {
      console.error('saveJob: Error saving job:', insertError);
      return false;
    }
    
    console.log(`saveJob: Successfully saved job ${jobId} for user ${userId}`);
    return true;
  } catch (error) {
    console.error('saveJob: Unexpected error:', error);
    return false;
  }
};

/**
 * Remove a saved job for a user
 * @param {string} userId - The user ID
 * @param {string} jobId - The job ID
 * @returns {Promise<boolean>} Success state
 */
export const removeSavedJob = async (userId, jobId) => {
  if (!userId) {
    console.error('removeSavedJob: userId is missing');
    return false;
  }
  
  // Make sure we have a valid job ID - not undefined, null, or 0
  if (jobId === undefined || jobId === null || jobId === 0) {
    console.error(`removeSavedJob: Invalid jobId provided: ${jobId}`);
    return false;
  }
  
  console.log(`removeSavedJob: Removing job ${jobId} for user ${userId}`);
  
  try {
    // First check if this job exists for the user
    const { data: checkData, error: checkError } = await supabase
      .from(SAVED_JOBS_TABLE)
      .select('id')
      .eq('user_id', userId)
      .eq(ID_FIELD, jobId);
      
    if (checkError) {
      console.error('removeSavedJob: Error checking if job exists:', checkError);
    }
    
    if (!checkData || checkData.length === 0) {
      console.log(`removeSavedJob: Job ${jobId} not found for user ${userId}`);
      return true; // Return true because the end state is as expected - not saved
    }
    
    // Delete the job
    const { error: deleteError } = await supabase
      .from(SAVED_JOBS_TABLE)
      .delete()
      .eq('user_id', userId)
      .eq(ID_FIELD, jobId);
      
    if (deleteError) {
      console.error('removeSavedJob: Error deleting job:', deleteError);
      return false;
    }
    
    console.log(`removeSavedJob: Successfully removed job ${jobId} for user ${userId}`);
    return true;
  } catch (error) {
    console.error('removeSavedJob: Unexpected error:', error);
    return false;
  }
};